import React from 'react';
import { 
  Sparkles, 
  Dumbbell, 
  TreePine, 
  Brain, 
  DollarSign, 
  Trash2, 
  Users, 
  Shirt,
  BookOpen,
  Headphones
} from 'lucide-react';
import { HabitCheckbox } from './HabitCheckbox';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';

export const DailyHabits = ({ data, settings, onUpdate }) => {
  return (
    <div className="space-y-6" data-testid="daily-habits-section">
      {/* Personal Care */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyan-400" />
          Personal Care
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <HabitCheckbox
            checked={data.brushedTeethAM}
            onChange={(val) => onUpdate({ brushedTeethAM: val })}
            label="Brushed Teeth"
            sublabel="Morning"
            color="cyan"
            testId="habit-teeth-am"
          />
          <HabitCheckbox
            checked={data.brushedTeethPM}
            onChange={(val) => onUpdate({ brushedTeethPM: val })}
            label="Brushed Teeth"
            sublabel="Evening"
            color="cyan"
            testId="habit-teeth-pm"
          />
          <HabitCheckbox
            checked={data.skinCareAM}
            onChange={(val) => onUpdate({ skinCareAM: val })}
            label="Skin Care"
            sublabel="Morning routine"
            color="purple"
            testId="habit-skincare-am"
          />
          <HabitCheckbox
            checked={data.skinCarePM}
            onChange={(val) => onUpdate({ skinCarePM: val })}
            label="Skin Care"
            sublabel="Evening routine"
            color="purple"
            testId="habit-skincare-pm"
          />
        </div>
      </div>

      {/* Physical & Mental */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-lime-400" />
          Physical & Mental
        </h3>
        <div className="space-y-3">
          <HabitCheckbox
            checked={data.gym}
            onChange={(val) => onUpdate({ gym: val })}
            label="Gym / Workout"
            sublabel="Move your body"
            icon={Dumbbell}
            color="lime"
            testId="habit-gym"
          />
          <HabitCheckbox
            checked={data.nature}
            onChange={(val) => onUpdate({ nature: val })}
            label="Go Outside in Nature"
            sublabel="Fresh air & grounding"
            icon={TreePine}
            color="lime"
            testId="habit-nature"
          />
          <HabitCheckbox
            checked={data.practicedSkill}
            onChange={(val) => onUpdate({ practicedSkill: val })}
            label="Practiced a Skill"
            sublabel="Continuous improvement"
            icon={Brain}
            color="purple"
            testId="habit-skill"
          />
        </div>
      </div>

      {/* Reading & Listening */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-amber-400" />
          Reading & Listening
        </h3>
        
        {/* Current Book */}
        <div className="mb-4">
          <label className="text-sm text-white/50 mb-2 block">Current Book</label>
          <Input
            value={data.currentBook || ''}
            onChange={(e) => onUpdate({ currentBook: e.target.value })}
            placeholder="What are you reading/listening to?"
            className="input-futuristic"
            data-testid="current-book-input"
          />
        </div>

        {/* Reading Progress */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-white/50 mb-2 block">Pages Read Today</label>
            <div className="flex items-center gap-3">
              <Slider
                value={[data.readPages || 0]}
                onValueChange={([value]) => onUpdate({ readPages: value })}
                min={0}
                max={50}
                step={1}
                className="flex-1"
                data-testid="read-pages-slider"
              />
              <span className={`font-mono text-xl min-w-12 text-right ${
                data.readPages >= (settings?.readingGoalPages || 10) ? 'text-lime-400' : 'text-white/70'
              }`}>
                {data.readPages || 0}
              </span>
            </div>
            <p className="text-xs text-white/30 mt-1">
              Goal: {settings?.readingGoalPages || 10} pages
            </p>
          </div>

          <div>
            <label className="text-sm text-white/50 mb-2 block">Audible Minutes</label>
            <div className="flex items-center gap-3">
              <Slider
                value={[data.audibleMinutes || 0]}
                onValueChange={([value]) => onUpdate({ audibleMinutes: value })}
                min={0}
                max={120}
                step={5}
                className="flex-1"
                data-testid="audible-slider"
              />
              <span className={`font-mono text-xl min-w-12 text-right ${
                data.audibleMinutes >= (settings?.audibleGoalMinutes || 60) ? 'text-lime-400' : 'text-white/70'
              }`}>
                {data.audibleMinutes || 0}
              </span>
            </div>
            <p className="text-xs text-white/30 mt-1">
              Goal: {settings?.audibleGoalMinutes || 60} min
            </p>
          </div>
        </div>

        {/* Pages Left */}
        <div>
          <label className="text-sm text-white/50 mb-2 block">Pages/Time Left in Book</label>
          <Input
            value={data.bookPagesLeft || ''}
            onChange={(e) => onUpdate({ bookPagesLeft: e.target.value })}
            placeholder="e.g., 120 pages or 3 hours left"
            className="input-futuristic"
            data-testid="pages-left-input"
          />
        </div>
      </div>

      {/* Productivity & Growth */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-amber-400" />
          Productivity & Growth
        </h3>
        
        {/* Money Move */}
        <div className="mb-4">
          <label className="text-sm text-white/50 mb-2 block">Made a Money Move</label>
          <Input
            value={data.moneyMove || ''}
            onChange={(e) => onUpdate({ moneyMove: e.target.value })}
            placeholder="Invested, invoiced, pitched, saved, built, followed up..."
            className="input-futuristic"
            data-testid="money-move-input"
          />
          <p className="text-xs text-white/30 mt-1">
            What did you do to move the needle financially?
          </p>
        </div>

        <div className="space-y-3">
          <HabitCheckbox
            checked={data.decluttered}
            onChange={(val) => onUpdate({ decluttered: val })}
            label="Decluttered or Cleaned Up"
            sublabel="Clear space, clear mind"
            icon={Trash2}
            color="amber"
            testId="habit-declutter"
          />
          <HabitCheckbox
            checked={data.connectedWithSomeone}
            onChange={(val) => onUpdate({ connectedWithSomeone: val })}
            label="Connected with Someone"
            sublabel="Meaningful interaction"
            icon={Users}
            color="rose"
            testId="habit-connect"
          />
          <HabitCheckbox
            checked={data.preppedClothes}
            onChange={(val) => onUpdate({ preppedClothes: val })}
            label="Prepped Clothes for Tomorrow"
            sublabel="Set up for success"
            icon={Shirt}
            color="cyan"
            testId="habit-clothes"
          />
        </div>
      </div>
    </div>
  );
};

export default DailyHabits;
